import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  FormHelperText,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Assignment as AssignmentIcon,
  AutoFixHigh as AutoFixHighIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  ListAlt as ListAltIcon,
  Refresh as RefreshIcon,
  WarningAmber as WarningAmberIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import {
  criarInscricao,
  inscreverProduto,
  listarGruposDisponiveis,
  obterContextoInscricao,
  obterFormularioSubcategoria,
} from '../../api/inscricoes';
import { finalizarEnquadramento, obterBootstrapEnquadramento, queryEnquadramento } from '../../api/enquadramentos';
import FormularioDinamico from '../../components/forms/FormularioDinamico';
import ResultadoDialog from '../../components/dialogs/ResultadoDialog';
import { SkeletonBlock } from '../../components/LoadingSkeleton';
import PageHeader from '../../components/PageHeader';
import { extrairMensagemErro } from '../../utils/apiError';

// ─── helpers ────────────────────────────────────────────────────────────────

function parseCampos(rawCampos = []) {
  return rawCampos.map((c) => ({
    codigo: c.codigo || '',
    label: c.label || c.codigo || '',
    tipo: c.tipo || 'text',
    obrigatorio: !!c.obrigatorio,
    ordem: c.ordem ?? 0,
    placeholder: c.placeholder || undefined,
    opcoes: c.opcoesJson
      ? (() => {
          try {
            return JSON.parse(c.opcoesJson);
          } catch {
            return null;
          }
        })()
      : c.opcoes || null,
    configuracao: c.configuracaoJson
      ? (() => {
          try {
            return JSON.parse(c.configuracaoJson);
          } catch {
            return null;
          }
        })()
      : c.configuracao || null,
  }));
}

const PASSOS_KEYS = ['inscricao.nova.passo.classificacao', 'inscricao.nova.passo.dados'];

// ─── sub-component: BatchCard (grupo de perguntas do wizard) ─────────────────

function BatchCard({ titulo, campos, valores, onChange, erros = [], readonly = false, onEditar }) {
  const { t } = useTranslation();
  const done = readonly;
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: done ? 'success.light' : 'primary.main',
        borderRadius: 1.5,
        overflow: 'hidden',
        transition: 'border-color 0.2s',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: (theme) => alpha(done ? theme.palette.success.main : theme.palette.primary.main, 0.06),
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: done ? 'success.light' : 'primary.light',
        }}
      >
        {done ? (
          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
        ) : (
          <AssignmentIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        )}
        <Typography variant="caption" fontWeight={700} color={done ? 'success.dark' : 'primary.dark'}>
          {titulo}
        </Typography>
        {done && onEditar && (
          <Button
            size="small"
            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
            onClick={onEditar}
            sx={{ ml: 'auto', py: 0, minHeight: 0 }}
          >
            {t('Edit')}
          </Button>
        )}
      </Box>

      <Box sx={{ p: 2 }}>
        {readonly ? (
          <Stack spacing={0.5}>
            {campos.map((c) => (
              <Typography key={c.codigo} variant="body2">
                <strong>{c.label}:</strong>{' '}
                {(() => {
                  const val = valores[c.codigo];
                  const opt = c.opcoes?.find((o) => o.valor === val);
                  return opt ? opt.label || opt.valor : val || '—';
                })()}
              </Typography>
            ))}
          </Stack>
        ) : (
          <Stack spacing={2}>
            {[...campos]
              .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
              .map((c) => {
                const valor = valores[c.codigo] || '';
                const label = c.label || c.codigo;
                const tipo = (c.tipo || 'text').toLowerCase();
                const hasError = erros.includes(c.codigo);

                if (tipo === 'select' || tipo === 'lista') {
                  return (
                    <Box key={c.codigo}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                        {label}
                        {c.obrigatorio && ' *'}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                        {(c.opcoes || []).map((o) => (
                          <Chip
                            key={o.valor}
                            label={o.label || o.valor}
                            variant={valor === o.valor ? 'filled' : 'outlined'}
                            color={valor === o.valor ? 'primary' : 'default'}
                            onClick={() => onChange(c.codigo, o.valor)}
                            sx={{
                              cursor: 'pointer',
                              fontSize: { xs: '0.875rem', sm: '0.8125rem' },
                              height: { xs: 40, sm: 32 },
                            }}
                          />
                        ))}
                      </Stack>
                      {hasError && <FormHelperText error>{t('inscricao.nova.campoObrigatorio')}</FormHelperText>}
                    </Box>
                  );
                }

                return (
                  <TextField
                    key={c.codigo}
                    label={label}
                    value={valor}
                    onChange={(e) => onChange(c.codigo, e.target.value)}
                    type={tipo === 'number' ? 'number' : tipo === 'date' ? 'date' : 'text'}
                    size="small"
                    fullWidth
                    required={c.obrigatorio}
                    error={hasError}
                    helperText={hasError ? t('inscricao.nova.campoObrigatorio') : undefined}
                    placeholder={c.placeholder}
                    InputLabelProps={tipo === 'date' ? { shrink: true } : undefined}
                  />
                );
              })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

// ─── sub-component: ClassificacaoResultCard ──────────────────────────────────

function ClassificacaoResultCard({ classificacao, onReset }) {
  const { t } = useTranslation();
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'success.light',
        borderRadius: 1.5,
        p: 2,
        bgcolor: (theme) => alpha(theme.palette.success.main, 0.06),
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <CheckCircleIcon sx={{ color: 'success.main', mt: 0.25, flexShrink: 0 }} />
        <Box flex={1} minWidth={0}>
          <Typography variant="subtitle2" fontWeight={700} color="success.dark">
            {t('inscricao.nova.classificada.titulo')}
          </Typography>
          <Typography variant="body2" color="success.dark" mt={0.25} noWrap>
            {classificacao.subcategoriaNome}
            {classificacao.grupoNome && classificacao.grupoNome !== classificacao.subcategoriaNome && (
              <> · {classificacao.grupoNome}</>
            )}
          </Typography>
        </Box>
        {onReset && (
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onReset}
            color="inherit"
            sx={{ flexShrink: 0, fontSize: 12 }}
          >
            {t('inscricao.nova.refazer')}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function NovoInscricaoProduto() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── Contexto ──
  const [loadingContexto, setLoadingContexto] = useState(true);
  const [contexto, setContexto] = useState(null);
  const [contextoError, setContextoError] = useState(null);

  // ── Navegação ──
  const [passo, setPasso] = useState(0);
  const [abaAtiva, setAbaAtiva] = useState(0); // 0=wizard, 1=direto

  // ── Classificação resolvida ──
  const [classificacao, setClassificacao] = useState(null);

  // ── Wizard ──
  const [wizardStatus, setWizardStatus] = useState('loading');
  const [wizardCamposAtuais, setWizardCamposAtuais] = useState([]);
  const [wizardRespostas, setWizardRespostas] = useState({});
  const [wizardBatchesAnteriores, setWizardBatchesAnteriores] = useState([]);
  const [wizardCandidatas, setWizardCandidatas] = useState([]);
  const [wizardDiagnostico, setWizardDiagnostico] = useState(null);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardCamposErros, setWizardCamposErros] = useState([]);

  // ── Seleção Direta ──
  const [grupos, setGrupos] = useState([]);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [grupoSelecionado, setGrupoSelecionado] = useState(null);
  // Quando a subcategoria escolhida tem N grupos, segura ela aqui pra renderizar
  // um segundo combo de "variação" antes de definir grupoSelecionado.
  const [subcategoriaInterimId, setSubcategoriaInterimId] = useState(null);

  // ── Formulário produto ──
  const [formCampos, setFormCampos] = useState([]);
  const [formValores, setFormValores] = useState({});
  const [nome, setNome] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [loadingFormProduto, setLoadingFormProduto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resultadoEnvio, setResultadoEnvio] = useState(null);
  const [nomeError, setNomeError] = useState(false);
  const [descricaoError, setDescricaoError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [hasDateErrors, setHasDateErrors] = useState(false);

  // ── Effects ──
  useEffect(() => {
    carregarContexto();
  }, []);

  useEffect(() => {
    if (!contexto) return;
    const modo = contexto.cadastroProdutoModo;
    if (modo === 1 || modo === 3) iniciarWizard();
    if (modo === 2 || modo === 3) carregarGrupos();
    if (modo === 2) setAbaAtiva(1);
  }, [contexto]);

  useEffect(() => {
    if (!classificacao?.subcategoriaId) return;
    carregarFormulario(classificacao.subcategoriaId);
  }, [classificacao?.subcategoriaId]);

  // ── Loaders ──
  const carregarContexto = async () => {
    setLoadingContexto(true);
    setContextoError(null);
    try {
      const res = await obterContextoInscricao();
      setContexto(res.data?.data ?? res.data ?? null);
    } catch {
      setContextoError(t('inscricao.nova.erroCarga'));
    } finally {
      setLoadingContexto(false);
    }
  };

  const iniciarWizard = useCallback(async () => {
    if (!contexto?.concursoId) return;
    setWizardStatus('loading');
    setWizardLoading(true);
    setWizardCamposAtuais([]);
    setWizardRespostas({});
    setWizardBatchesAnteriores([]);
    setWizardCandidatas([]);
    setWizardDiagnostico(null);
    setWizardCamposErros([]);
    try {
      const res = await obterBootstrapEnquadramento(contexto.concursoId);
      const data = res.data?.data ?? res.data;
      const campos = parseCampos(data?.campos || []);
      if (campos.length === 0) {
        await executarFinalizacao({}, contexto.concursoId);
      } else {
        setWizardCamposAtuais(campos);
        setWizardStatus('respondendo');
      }
    } catch {
      setWizardStatus('erro');
    } finally {
      setWizardLoading(false);
    }
  }, [contexto?.concursoId]);

  const carregarGrupos = async () => {
    setLoadingGrupos(true);
    try {
      const res = await listarGruposDisponiveis();
      setGrupos(res.data?.data || res.data || []);
    } catch {
      /* silently fail */
    } finally {
      setLoadingGrupos(false);
    }
  };

  const carregarFormulario = async (subcategoriaId) => {
    if (!subcategoriaId) return;
    setLoadingFormProduto(true);
    setFormCampos([]);
    setFormValores({});
    try {
      const res = await obterFormularioSubcategoria(subcategoriaId);
      const data = res.data?.data ?? res.data;
      setFormCampos(parseCampos(data?.campos || []));
    } catch {
      /* 404 = sem formulário dinâmico */
    } finally {
      setLoadingFormProduto(false);
    }
  };

  // ── Wizard handlers ──
  const handleWizardAvancar = async () => {
    // Toda pergunta do wizard encaminha o produto — sem resposta não dá pra avançar.
    // (A API do wizard não manda `obrigatorio`; antes, tudo passava e a mesma pergunta
    // voltava e era empilhada como etapa nova, sem fim.)
    const faltando = wizardCamposAtuais.filter((c) => !wizardRespostas[c.codigo]?.trim());
    if (faltando.length > 0) {
      setWizardCamposErros(faltando.map((c) => c.codigo));
      return;
    }
    setWizardCamposErros([]);
    setWizardLoading(true);
    try {
      const todasRespostas = Object.entries(wizardRespostas).map(([campoCodigo, optionKey]) => ({
        campoCodigo,
        optionKey,
      }));
      const res = await queryEnquadramento(contexto.concursoId, { respostas: todasRespostas });
      const data = res.data?.data ?? res.data;
      const jaPerguntados = new Set([
        ...wizardBatchesAnteriores.flatMap((b) => b.campos.map((c) => c.codigo)),
        ...wizardCamposAtuais.map((c) => c.codigo),
      ]);
      const novoCampos = parseCampos(data?.campos || []).filter((c) => !jaPerguntados.has(c.codigo));
      if (novoCampos.length > 0) {
        setWizardBatchesAnteriores((prev) => [
          ...prev,
          { campos: wizardCamposAtuais, respostas: { ...wizardRespostas } },
        ]);
        setWizardCamposAtuais(novoCampos);
      } else {
        await executarFinalizacao(wizardRespostas, contexto.concursoId);
      }
    } catch {
      setWizardStatus('erro');
    } finally {
      setWizardLoading(false);
    }
  };

  const executarFinalizacao = async (respostas, concursoId) => {
    // A API espera { campoCodigo, opcaoKey }; a inscrição do produto usa { codigoCampo, valor }.
    const respostasInscricao = Object.entries(respostas).map(([codigoCampo, valor]) => ({ codigoCampo, valor }));
    const payload = {
      concursoId,
      respostas: respostasInscricao.map((r) => ({ campoCodigo: r.codigoCampo, opcaoKey: r.valor })),
    };
    try {
      const res = await finalizarEnquadramento(payload);
      const data = res.data?.data ?? res.data;
      if (data.status === 'finalizado') {
        setWizardStatus('finalizado');
        setClassificacao({
          subcategoriaId: data.final.subcategoriaId,
          enquadramentoGrupoId: data.final.enquadramentoGrupoId,
          subcategoriaNome: data.final.subcategoriaNome || '—',
          grupoNome: data.final.grupoNome || '',
          respostasEnquadramento: respostasInscricao,
        });
      } else if (data.status === 'pendente') {
        const candidatas = data.subcategoriasCandidatas || [];
        const flatGroups = candidatas.flatMap((s) =>
          (s.grupos || []).map((g) => ({
            ...g,
            subcategoriaId: s.subcategoriaId,
            subcategoriaNome: s.nome,
          })),
        );
        if (flatGroups.length === 1) {
          const g = flatGroups[0];
          setWizardStatus('finalizado');
          setClassificacao({
            subcategoriaId: g.subcategoriaId,
            enquadramentoGrupoId: g.enquadramentoGrupoId,
            subcategoriaNome: g.subcategoriaNome || '—',
            grupoNome: g.nome || g.codigo || '—',
            respostasEnquadramento: respostasInscricao,
          });
        } else {
          setWizardCandidatas(candidatas);
          setWizardStatus('pendente');
        }
      } else {
        setWizardDiagnostico(data.diagnostico);
        setWizardStatus('sem_match');
      }
    } catch {
      setWizardStatus('erro');
    }
  };

  const handleSelecionarCandidataGrupo = (subcategoriaId, subcategoriaNome, grupo) => {
    setClassificacao({
      subcategoriaId,
      enquadramentoGrupoId: grupo.enquadramentoGrupoId,
      subcategoriaNome: subcategoriaNome || '—',
      grupoNome: grupo.nome || grupo.codigo || '—',
      respostasEnquadramento: Object.entries(wizardRespostas).map(([codigoCampo, valor]) => ({
        codigoCampo,
        valor,
      })),
    });
    setWizardStatus('finalizado');
  };

  const handleEditarEtapa = (indice) => {
    const alvo = wizardBatchesAnteriores[indice];
    if (!alvo) return;
    const manter = new Set(wizardBatchesAnteriores.slice(0, indice + 1).flatMap((b) => b.campos.map((c) => c.codigo)));
    setWizardRespostas((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => manter.has(k))));
    setWizardCamposAtuais(alvo.campos);
    setWizardBatchesAnteriores((prev) => prev.slice(0, indice));
    setWizardCamposErros([]);
  };

  const handleResetWizard = () => {
    setClassificacao(null);
    iniciarWizard();
  };

  // ── Direct selection ──
  const handleGrupoDireto = (grupo) => {
    setGrupoSelecionado(grupo);
    setClassificacao(
      grupo
        ? {
            subcategoriaId: grupo.subcategoriaId,
            enquadramentoGrupoId: grupo.enquadramentoGrupoId,
            subcategoriaNome: grupo.subcategoriaNome || '—',
            grupoNome: grupo.grupoNome || grupo.grupoCodigo || '—',
            respostasEnquadramento: [],
          }
        : null,
    );
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!nome.trim()) {
      setNomeError(true);
      return;
    }
    setNomeError(false);
    if (contexto?.descricaoProdutoObrigatoria && !observacoes.trim()) {
      setDescricaoError(true);
      return;
    }
    setDescricaoError(false);
    if (hasDateErrors) return;
    const faltando = formCampos.filter((c) => c.obrigatorio && !formValores[c.codigo]?.trim()).map((c) => c.codigo);
    if (faltando.length > 0) {
      setFieldErrors(faltando);
      return;
    }
    setFieldErrors([]);
    setSubmitting(true);
    try {
      if (!contexto.minhaInscricaoId) {
        await criarInscricao({ paisCodigo: 'BR', moeda: 'BRL' });
      }
      await inscreverProduto({
        subcategoriaId: classificacao.subcategoriaId,
        enquadramentoGrupoId: classificacao.enquadramentoGrupoId,
        nome: nome.trim(),
        observacoes: observacoes.trim() || null,
        treinamento: false,
        valores: formCampos.map((c) => ({
          codigoCampo: c.codigo,
          valor: formValores[c.codigo] || '',
        })),
        respostasEnquadramento: classificacao.respostasEnquadramento || [],
      });
      setResultadoEnvio({
        sucesso: true,
        titulo: t('inscricao.nova.feedback.sucesso'),
        rotuloAcao: t('inscricao.nova.feedback.verProdutos'),
        onAcao: () => navigate('/inscricoes'),
      });
    } catch (err) {
      setResultadoEnvio({ sucesso: false, mensagem: extrairMensagemErro(err, t('inscricao.nova.erroSubmit')) });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Computed ──
  const modo = contexto?.cadastroProdutoModo;
  const mostrarTabs = modo === 3;

  // Page ready: aguarda contexto + dependências iniciais (wizard bootstrap / grupos)
  // conforme o modo. Uma vez pronto, fica pronto (refs garantem que refazer wizard
  // não re-dispara o skeleton da página toda).
  const wizardAtivo = modo === 1 || modo === 3;
  const diretoAtivo = modo === 2 || modo === 3;
  const pageReadyNow =
    !loadingContexto && (!wizardAtivo || wizardStatus !== 'loading') && (!diretoAtivo || !loadingGrupos);
  const hasBeenReady = useRef(false);
  if (pageReadyNow) hasBeenReady.current = true;
  const pageVisible = pageReadyNow || hasBeenReady.current;

  // ── Error / Fechado ──
  if (contextoError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={carregarContexto}>
              {t('inscricao.nova.wizard.tentarNovamente')}
            </Button>
          }
        >
          {contextoError}
        </Alert>
      </Box>
    );
  }
  // if (!contexto?.inscricoesAbertas) {
  //   return (
  //     <Box sx={{ p: 3, maxWidth: 480, mx: "auto" }}>
  //       <Alert severity="warning" icon={<WarningAmberIcon />}>
  //         <Typography fontWeight={600}>Inscrições encerradas</Typography>
  //         <Typography variant="body2" mt={0.5}>
  //           O período de inscrições deste concurso está encerrado.
  //         </Typography>
  //       </Alert>
  //       <Button
  //         startIcon={<ArrowBackIcon />}
  //         onClick={() => navigate("/inscricoes")}
  //         sx={{ mt: 2 }}
  //       >
  //         Voltar para minhas inscrições
  //       </Button>
  //     </Box>
  //   );
  // }

  // ── Wizard content ──
  const renderWizardContent = () => {
    if (wizardStatus === 'loading') {
      return (
        <Stack spacing={2}>
          <SkeletonBlock height={40} width="40%" />
          <SkeletonBlock height={180} />
        </Stack>
      );
    }
    if (wizardStatus === 'erro') {
      return (
        <Alert
          severity="error"
          action={
            <Button size="small" color="inherit" onClick={iniciarWizard}>
              {t('inscricao.nova.wizard.tentarNovamente')}
            </Button>
          }
        >
          {t('inscricao.nova.wizard.erroCarga')}
        </Alert>
      );
    }
    if (wizardStatus === 'finalizado' && classificacao) {
      return <ClassificacaoResultCard classificacao={classificacao} onReset={handleResetWizard} />;
    }
    if (wizardStatus === 'sem_match') {
      return (
        <Stack spacing={2}>
          <Alert severity="error" icon={<WarningAmberIcon />}>
            <Typography fontWeight={600}>{t('inscricao.nova.wizard.semMatch.titulo')}</Typography>
            {wizardDiagnostico?.explicacao && (
              <Typography variant="body2" mt={0.5}>
                {wizardDiagnostico.explicacao}
              </Typography>
            )}
          </Alert>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleResetWizard}>
            {t('inscricao.nova.wizard.refazerClassificacao')}
          </Button>
        </Stack>
      );
    }
    if (wizardStatus === 'pendente') {
      return (
        <Stack spacing={2}>
          <Alert severity="info">{t('inscricao.nova.wizard.multiplosResultados')}</Alert>
          {wizardCandidatas.map((candidata) => (
            <Box key={candidata.subcategoriaId}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ display: 'block', mb: 0.75, pl: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {candidata.nome || candidata.codigo}
              </Typography>
              <Stack spacing={1}>
                {(candidata.grupos || []).map((grupo) => (
                  <Card
                    key={grupo.enquadramentoGrupoId}
                    variant="outlined"
                    sx={{
                      'borderRadius': 1.5,
                      'transition': 'border-color 0.15s, box-shadow 0.15s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: theme.shadows[2],
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleSelecionarCandidataGrupo(candidata.subcategoriaId, candidata.nome, grupo)}
                      sx={{ px: 2, py: 1.5 }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Typography variant="body2" fontWeight={500}>
                          {grupo.nome || grupo.codigo}
                        </Typography>
                        {grupo.certificado && (
                          <Chip
                            icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                            label={t('inscricao.nova.wizard.certificado')}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      );
    }

    // 'respondendo'
    return (
      <Stack spacing={2}>
        {wizardBatchesAnteriores.map((batch, i) => (
          <Fade in key={i}>
            <div>
              <BatchCard
                titulo={`${t('inscricao.nova.wizard.etapa')} ${i + 1}`}
                campos={batch.campos}
                valores={batch.respostas}
                onChange={() => {}}
                readonly
                onEditar={wizardLoading ? undefined : () => handleEditarEtapa(i)}
              />
            </div>
          </Fade>
        ))}

        {wizardCamposAtuais.length > 0 && (
          <Fade in>
            <div>
              <BatchCard
                titulo={`${t('inscricao.nova.wizard.etapa')} ${wizardBatchesAnteriores.length + 1}`}
                campos={wizardCamposAtuais}
                valores={wizardRespostas}
                onChange={(codigo, valor) => setWizardRespostas((prev) => ({ ...prev, [codigo]: valor }))}
                erros={wizardCamposErros}
              />
            </div>
          </Fade>
        )}

        {wizardLoading && <LinearProgress sx={{ borderRadius: 1 }} />}

        <Box display="flex" justifyContent={{ xs: 'stretch', sm: 'flex-end' }}>
          <Button
            variant="contained"
            size={isMobile ? 'large' : 'medium'}
            endIcon={<ArrowForwardIcon />}
            onClick={handleWizardAvancar}
            disabled={wizardLoading}
            fullWidth={isMobile}
          >
            {wizardLoading ? t('inscricao.nova.wizard.aguarde') : t('inscricao.nova.wizard.avancar')}
          </Button>
        </Box>
      </Stack>
    );
  };

  // ── Direct content ──
  const renderDiretoContent = () => {
    if (loadingGrupos) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (grupos.length === 0) {
      return <Alert severity="info">{t('inscricao.nova.direto.semGrupos')}</Alert>;
    }

    // Deduplica por subcategoria — 1 linha por SubcategoriaId, com lista interna
    // de grupos ("variações"). Se a subcategoria tem só 1 grupo, seleção direta.
    const subcategoriasMap = new Map();
    for (const g of grupos) {
      if (!subcategoriasMap.has(g.subcategoriaId)) {
        subcategoriasMap.set(g.subcategoriaId, {
          subcategoriaId: g.subcategoriaId,
          subcategoriaNome: g.subcategoriaNome || '—',
          _grupos: [],
        });
      }
      subcategoriasMap.get(g.subcategoriaId)._grupos.push(g);
    }
    const subcategoriaOptions = Array.from(subcategoriasMap.values()).sort((a, b) =>
      a.subcategoriaNome.localeCompare(b.subcategoriaNome, 'pt-BR', { sensitivity: 'base' }),
    );
    // Ordena também as variações (grupos) dentro de cada subcategoria
    for (const sub of subcategoriaOptions) {
      sub._grupos.sort((a, b) =>
        (a.grupoNome || a.grupoCodigo || '').localeCompare(b.grupoNome || b.grupoCodigo || '', 'pt-BR', {
          sensitivity: 'base',
        }),
      );
    }

    const subcategoriaSelecionadaObj = (() => {
      const id = grupoSelecionado?.subcategoriaId ?? subcategoriaInterimId;
      return id ? (subcategoriasMap.get(id) ?? null) : null;
    })();

    const handleSelecionarSubcategoria = (sub) => {
      if (!sub) {
        setSubcategoriaInterimId(null);
        handleGrupoDireto(null);
        return;
      }
      if (sub._grupos.length === 1) {
        setSubcategoriaInterimId(null);
        handleGrupoDireto(sub._grupos[0]);
      } else {
        setSubcategoriaInterimId(sub.subcategoriaId);
        handleGrupoDireto(null);
      }
    };

    return (
      <Stack spacing={2}>
        <Autocomplete
          options={subcategoriaOptions}
          getOptionLabel={(o) => o.subcategoriaNome}
          isOptionEqualToValue={(o, v) => o.subcategoriaId === v.subcategoriaId}
          value={subcategoriaSelecionadaObj}
          onChange={(_, val) => handleSelecionarSubcategoria(val)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('inscricao.nova.direto.label')}
              placeholder={t('inscricao.nova.direto.placeholder')}
              size={isMobile ? 'medium' : 'small'}
            />
          )}
          noOptionsText={t('inscricao.nova.direto.semOpcoes')}
        />

        {/* Combo secundário só aparece quando a subcategoria escolhida tem 2+ grupos */}
        {subcategoriaSelecionadaObj && subcategoriaSelecionadaObj._grupos.length > 1 && (
          <Autocomplete
            options={subcategoriaSelecionadaObj._grupos}
            getOptionLabel={(o) => o.grupoNome || o.grupoCodigo || '—'}
            isOptionEqualToValue={(o, v) => o.enquadramentoGrupoId === v.enquadramentoGrupoId}
            value={
              grupoSelecionado
                ? subcategoriaSelecionadaObj._grupos.find(
                    (g) => g.enquadramentoGrupoId === grupoSelecionado.enquadramentoGrupoId,
                  ) || null
                : null
            }
            onChange={(_, val) => handleGrupoDireto(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('inscricao.nova.direto.variacao', 'Variação')}
                size={isMobile ? 'medium' : 'small'}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Stack>
                  <Typography variant="body2">{option.grupoNome || option.grupoCodigo}</Typography>
                  {option.grupoDescricao && (
                    <Typography variant="caption" color="text.secondary">
                      {option.grupoDescricao}
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          />
        )}
      </Stack>
    );
  };

  // ── Step 0 ──
  const renderPasso0 = () => (
    <Stack spacing={3}>
      {mostrarTabs ? (
        <>
          <Tabs
            value={abaAtiva}
            onChange={(_, v) => setAbaAtiva(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<AutoFixHighIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={t('inscricao.nova.tabs.wizard')}
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            />
            <Tab
              icon={<ListAltIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={t('inscricao.nova.tabs.direto')}
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            />
          </Tabs>
          <Box>
            {abaAtiva === 0 && renderWizardContent()}
            {abaAtiva === 1 && renderDiretoContent()}
          </Box>
        </>
      ) : modo === 1 ? (
        renderWizardContent()
      ) : (
        renderDiretoContent()
      )}

      <Collapse in={!!classificacao && mostrarTabs}>
        <Alert severity="success" icon={<CheckCircleIcon />}>
          {t('inscricao.nova.classificadoAlertPre')} <strong>{classificacao?.subcategoriaNome}</strong>.{' '}
          {t('inscricao.nova.classificadoAlertPos')} <strong>{t('inscricao.nova.acoes.proximaEtapa')}</strong>{' '}
          {t('inscricao.nova.classificadoAlertPos3')}
        </Alert>
      </Collapse>
    </Stack>
  );

  // ── Step 1 ──
  const renderPasso1 = () => (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'success.light',
          borderRadius: 1.5,
          p: 2,
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.06),
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle2" fontWeight={700} color="success.dark">
              {t('inscricao.nova.categoriaSelecionada')}
            </Typography>
            <Typography variant="body2" color="success.dark" mt={0.25} noWrap>
              {classificacao.subcategoriaNome}
              {classificacao.grupoNome && classificacao.grupoNome !== classificacao.subcategoriaNome && (
                <> · {classificacao.grupoNome}</>
              )}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Box>
        <Typography variant="h6" mb={2}>
          {t('inscricao.nova.form.identificacao')}
        </Typography>
        <Stack spacing={2}>
          <TextField
            label={t('inscricao.nova.form.nome')}
            value={nome}
            onChange={(e) => {
              setNome(e.target.value);
              if (e.target.value.trim()) setNomeError(false);
            }}
            error={nomeError}
            helperText={nomeError ? t('inscricao.nova.form.nomeObrigatorio') : undefined}
            size={isMobile ? 'medium' : 'small'}
            fullWidth
            autoFocus
            placeholder={t('inscricao.nova.form.nomePlaceholder')}
          />
          <TextField
            label={t('inscricao.nova.form.descricao', 'Descrição do produto')}
            value={observacoes}
            onChange={(e) => {
              setObservacoes(e.target.value);
              if (e.target.value.trim()) setDescricaoError(false);
            }}
            size={isMobile ? 'medium' : 'small'}
            fullWidth
            multiline
            minRows={3}
            required={!!contexto?.descricaoProdutoObrigatoria}
            error={descricaoError}
            helperText={
              descricaoError ? t('inscricao.nova.form.descricaoObrigatoria', 'Descrição é obrigatória') : undefined
            }
            placeholder={t('inscricao.nova.form.descricaoPlaceholder', 'Lote, informações adicionais...')}
          />
        </Stack>
      </Box>

      {loadingFormProduto ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : formCampos.length > 0 ? (
        <Box>
          <Typography variant="h6" mb={2}>
            {t('inscricao.nova.form.caracteristicas')}
          </Typography>
          <FormularioDinamico
            campos={formCampos}
            valores={formValores}
            onChange={(codigo, valor) => {
              setFormValores((prev) => ({ ...prev, [codigo]: valor }));
              setFieldErrors((prev) => prev.filter((c) => c !== codigo));
            }}
            onValidationChange={setHasDateErrors}
            isMobile={isMobile}
            gridTemplateColumns="1fr"
          />
          {fieldErrors.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('inscricao.nova.form.camposObrigatorios')}
            </Alert>
          )}
        </Box>
      ) : null}
    </Stack>
  );

  // ── Actions ──
  const renderActions = () => {
    const backBtn = (
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={passo === 0 ? () => navigate('/inscricoes') : () => setPasso(0)}
        color="inherit"
        size={isMobile ? 'large' : 'medium'}
        fullWidth={isMobile}
      >
        {passo === 0 ? t('inscricao.nova.acoes.cancelar') : t('inscricao.nova.acoes.voltar')}
      </Button>
    );

    const nextBtn =
      passo === 0 ? (
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => {
            if (classificacao) setPasso(1);
          }}
          disabled={!classificacao}
          size={isMobile ? 'large' : 'medium'}
          fullWidth={isMobile}
        >
          {t('inscricao.nova.acoes.proximaEtapa')}
        </Button>
      ) : (
        <Button
          variant="contained"
          startIcon={submitting ? undefined : <AssignmentIcon />}
          onClick={handleSubmit}
          disabled={submitting}
          size={isMobile ? 'large' : 'medium'}
          fullWidth={isMobile}
        >
          {submitting ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} color="inherit" />
              <span>{t('inscricao.nova.acoes.inscrevendo')}</span>
            </Stack>
          ) : (
            t('inscricao.nova.acoes.inscrever')
          )}
        </Button>
      );

    if (isMobile) {
      return (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 2,
            pb: 'max(16px, env(safe-area-inset-bottom))',
            boxShadow: `0 -4px 12px rgba(0,0,0,0.08)`,
          }}
        >
          <Stack spacing={1.5}>
            {nextBtn}
            {backBtn}
          </Stack>
        </Box>
      );
    }

    return (
      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={3}>
        {backBtn}
        {nextBtn}
      </Stack>
    );
  };

  // ── Full render ──
  return (
    <>
      <Helmet title={t('inscricao.nova.titulo')} />

      <Box
        sx={{
          maxWidth: 760,
          mx: 'auto',
          pb: isMobile ? '128px' : 4,
        }}
      >
        <PageHeader
          onBack={() => navigate('/inscricoes')}
          backLabel={t('inscricao.nova.breadcrumb')}
          title={isMobile ? t('inscricao.nova.tituloCurto') : t('inscricao.nova.titulo')}
        />

        {/* Stepper */}
        <Box sx={{ pb: 3 }}>
          <Stepper activeStep={passo} alternativeLabel={!isMobile}>
            {PASSOS_KEYS.map((key, i) => (
              <Step key={key} completed={passo > i}>
                <StepLabel>{t(key)}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Content */}
        <Card>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h5" mb={2.5}>
              {passo === 0 ? t('inscricao.nova.secao.categorias') : t('inscricao.nova.secao.dados')}
            </Typography>

            <Box sx={{ position: 'relative', minHeight: pageVisible ? 0 : 280 }}>
              <Fade in={!pageVisible} timeout={300} unmountOnExit>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 10,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack spacing={2}>
                    <SkeletonBlock height={40} width="40%" />
                    <SkeletonBlock height={180} />
                    <SkeletonBlock height={56} />
                  </Stack>
                </Box>
              </Fade>

              <Box
                sx={{
                  opacity: pageVisible ? 1 : 0,
                  transition: 'opacity 300ms ease-in',
                  pointerEvents: pageVisible ? 'auto' : 'none',
                }}
              >
                {pageVisible && (passo === 0 ? renderPasso0() : renderPasso1())}
              </Box>
            </Box>

            {!isMobile && (
              <Fade in={pageVisible} timeout={400} appear>
                <Box>
                  <Divider sx={{ mt: 3 }} />
                  {renderActions()}
                </Box>
              </Fade>
            )}
          </Box>
        </Card>
      </Box>

      {isMobile && (
        <Fade in={pageVisible} timeout={400} appear mountOnEnter>
          <Box>{renderActions()}</Box>
        </Fade>
      )}

      <ResultadoDialog resultado={resultadoEnvio} onClose={() => setResultadoEnvio(null)} />
    </>
  );
}
